import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, ShoppingBag, CreditCard, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { listOrders, listPayments, checkOrderStatus } from '../../features/orders/api/orders';
import { formatPrice } from '../../features/courses/api/courses';
import type { Order, Payment } from '../../types';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ORDER } from '../../constants/testIds';

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('id-ID') : '—');
const STATUS_OPTS = ['all', 'pending', 'paid', 'failed', 'expired'];
const inp = 'bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:border-cyan-400 outline-none';

export default function MemberOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');

  const filteredOrders = orders.filter((o) =>
    (statusFilter === 'all' || o.status === statusFilter) &&
    (q === '' || (o.product_title || '').toLowerCase().includes(q.toLowerCase()) || o.order_code.toLowerCase().includes(q.toLowerCase()))
  );

  useEffect(() => {
    if (params.get('payment') === 'success') {
      toast.success('Pembayaran berhasil', { description: 'Course Anda kini aktif. Cek di My Courses.' });
      params.delete('payment');
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Promise.all([listOrders(), listPayments()])
      .then(([o, p]) => {
        setOrders(o);
        setPayments(p);
      })
      .finally(() => setLoading(false));
  }, []);

  // Payment status poller: refresh any pending orders every 8s (auto marks paid without refresh)
  useEffect(() => {
    const hasPending = orders.some((o) => o.status === 'pending');
    if (!hasPending) return;
    const id = setInterval(async () => {
      const pending = orders.filter((o) => o.status === 'pending');
      let changed = false;
      for (const o of pending) {
        try {
          const updated = await checkOrderStatus(o.id);
          if (updated.status !== o.status) {
            changed = true;
            setOrders((cur) => cur.map((x) => (x.id === o.id ? updated : x)));
            if (updated.status === 'paid') toast.success('Pembayaran diterima', { description: `${updated.product_title || 'Pesanan'} kini aktif.` });
          }
        } catch {}
      }
      if (changed) listPayments().then(setPayments).catch(() => {});
    }, 8000);
    return () => clearInterval(id);
  }, [orders]);

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Orders & Payments">
      <div className="max-w-5xl" data-testid={ORDER.memberPage}>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-1">Riwayat</p>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Pesanan & Pembayaran</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Orders */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-2 text-white/70 flex-1">
                  <ShoppingBag className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
                  <h3 className="text-sm uppercase tracking-wider">Order History</h3>
                </div>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-white/30 absolute left-2.5 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                  <input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className={`${inp} pl-8 w-40`} />
                </div>
                <select data-testid="order-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inp}>
                  {STATUS_OPTS.map((s) => <option key={s} value={s}>{s === 'all' ? 'Semua Status' : s}</option>)}
                </select>
              </div>
              {filteredOrders.length === 0 ? (
                <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Tidak ada order.</p>
              ) : (
                <div className="space-y-2">
                  {filteredOrders.map((o) => (
                    <button key={o.id} data-testid={ORDER.orderRow} onClick={() => navigate(`/member/orders/${o.id}`)} className="w-full text-left flex items-center gap-4 bg-white/[0.02] border border-white/10 rounded-xl p-4 hover:border-cyan-400/40 transition-colors">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{o.product_title || o.product_type}</p>
                        <p className="text-xs text-white/40 font-mono">{o.order_code}</p>
                      </div>
                      <span className="text-cyan-400 font-medium text-sm">{formatPrice(o.amount, o.currency)}</span>
                      <StatusBadge status={o.status} />
                      <ChevronRight className="w-4 h-4 text-white/30" strokeWidth={1.5} />
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Payments */}
            <section>
              <div className="flex items-center gap-2 mb-3 text-white/70">
                <CreditCard className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
                <h3 className="text-sm uppercase tracking-wider">Payment History</h3>
              </div>
              {payments.length === 0 ? (
                <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada pembayaran.</p>
              ) : (
                <div className="space-y-2">
                  {payments.map((p) => (
                    <div key={p.id} data-testid={ORDER.paymentRow} className="flex items-center gap-4 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium capitalize">{p.gateway} · {p.payment_method}</p>
                        <p className="text-xs text-white/40">Dibayar: {fmtDate(p.paid_at)}</p>
                      </div>
                      <span className="text-cyan-400 font-medium text-sm">{formatPrice(p.amount, p.currency)}</span>
                      <StatusBadge status={p.payment_status} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
